import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { instrumentEmoji, instrumentLabel, useInstrument } from "../context/InstrumentContext";
import { useT } from "../context/LanguageContext";
import { Search, MapPin, GraduationCap, Phone, Mail, Globe } from "lucide-react";

export default function FindTeacherPage() {
  const t = useT();
  const { instruments: myInstruments, catalog } = useInstrument();
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [instrument, setInstrument] = useState(myInstruments[0] || "piano");
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    apiClient.get("/pricing/cities").then((r) => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  const instrumentOptions = useMemo(() => {
    const base = catalog && catalog.length ? catalog : [
      "piano","guitar","violin","cello","upright_bass","drums","flute",
      "saxophone","trumpet","clarinet","voice","ukulele",
    ];
    // Put "my instruments" first for convenience
    const mine = new Set(myInstruments || []);
    return [...(myInstruments || []), ...base.filter((k) => !mine.has(k))];
  }, [catalog, myInstruments]);

  const search = async (e) => {
    e?.preventDefault?.();
    setLoading(true); setSearched(true);
    try {
      const params = new URLSearchParams();
      if (city) params.set("city", city);
      if (instrument) params.set("instrument", instrument);
      const r = await apiClient.get(`/teachers/search?${params.toString()}`);
      setTeachers(r.data.teachers || []);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="find-teacher-page">
      <div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3">
          <GraduationCap className="w-8 h-8" /> {t("Find me a teacher")}
        </h1>
        <p style={{ color: "var(--app-text-muted)" }}>
          {t("Browse teachers in your city who are accepting students. Reach out, take a trial lesson, and join their studio when you're ready.")}
        </p>
      </div>

      <form onSubmit={search} className="bento-card p-5 grid md:grid-cols-4 gap-3 items-end" data-testid="find-teacher-filters">
        <div className="md:col-span-2">
          <Label>{t("City")}</Label>
          <Input
            list="city-options"
            placeholder={t("e.g. Nashville")}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="find-teacher-city"
          />
          <datalist id="city-options">
            {cities.map((c) => <option key={c.id || c.city} value={c.city} />)}
          </datalist>
        </div>
        <div>
          <Label>{t("Instrument")}</Label>
          <select
            value={instrument}
            onChange={(e) => setInstrument(e.target.value)}
            data-testid="find-teacher-instrument"
            className="w-full h-10 rounded-xl border-2 px-3 bg-transparent"
            style={{ borderColor: "var(--app-border)" }}
          >
            {instrumentOptions.map((k) => (
              <option key={k} value={k}>{instrumentLabel(k)}</option>
            ))}
          </select>
        </div>
        <Button type="submit" className="btn-pill btn-primary h-10 justify-center" data-testid="find-teacher-search" disabled={loading}>
          <Search className="w-4 h-4" /> {loading ? t("Searching...") : t("Search")}
        </Button>
      </form>

      {!searched ? (
        <div className="bento-card p-10 text-center">
          <MapPin className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--app-primary)" }} />
          <p style={{ color: "var(--app-text-muted)" }}>
            {t("Enter a city and instrument, then tap")} <b style={{ color: "var(--app-text)" }}>{t("Search")}</b>.
          </p>
        </div>
      ) : loading ? (
        <div className="text-center py-10" style={{ color: "var(--app-text-muted)" }}>{t("Searching…")}</div>
      ) : teachers.length === 0 ? (
        <div className="bento-card p-10 text-center">
          <div className="text-5xl mb-3">🎼</div>
          <p style={{ color: "var(--app-text-muted)" }}>
            {t("No teachers yet")} {city && <>{t("in")} <b style={{ color: "var(--app-text)" }}>{city}</b></>} {t("for")} <b style={{ color: "var(--app-text)" }}>{instrumentLabel(instrument)}</b>.
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--app-text-muted)" }}>
            {t("Try another city or instrument — our directory grows every week.")}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="find-teacher-results">
          {teachers.map((tch) => (
            <TeacherCard key={tch.id} t={tch} />
          ))}
        </div>
      )}
    </div>
  );
}

function TeacherCard({ t: teacher }) {
  const tr = useT();
  const L = teacher.listing || {};
  return (
    <div className="bento-card p-5" data-testid={`teacher-card-${teacher.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold truncate">{teacher.name}</div>
          {(L.city || L.state) && (
            <div className="text-sm flex items-center gap-1 mt-0.5" style={{ color: "var(--app-text-muted)" }}>
              <MapPin className="w-3.5 h-3.5" /> {[L.city, L.state].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1 justify-end">
          {(teacher.instruments || []).slice(0, 4).map((k) => (
            <span key={k} className="text-xs px-2 py-1 rounded-full border"
                  style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}
                  title={instrumentLabel(k)}>
              {instrumentEmoji(k)} {instrumentLabel(k)}
            </span>
          ))}
        </div>
      </div>
      {L.bio && <p className="mt-3 text-sm">{L.bio}</p>}
      <div className="mt-3 flex flex-wrap gap-3 text-sm">
        {L.hourly_rate_30min ? (
          <span className="font-semibold">${L.hourly_rate_30min} <span className="font-normal" style={{ color: "var(--app-text-muted)" }}>{tr("/ 30 min")}</span></span>
        ) : null}
        {L.hourly_rate_60min ? (
          <span className="font-semibold">${L.hourly_rate_60min} <span className="font-normal" style={{ color: "var(--app-text-muted)" }}>{tr("/ 60 min")}</span></span>
        ) : null}
        <div className="flex gap-2">
          {L.teaches_at_studio && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--app-surface-2)" }}>{tr("In-studio")}</span>
          )}
          {L.teaches_at_student && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--app-surface-2)" }}>{tr("At student's home")}</span>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {L.contact_email && (
          <a href={`mailto:${L.contact_email}`} className="btn-pill btn-primary text-sm"
             data-testid={`teacher-email-${teacher.id}`}>
            <Mail className="w-4 h-4" /> {tr("Email")}
          </a>
        )}
        {L.contact_phone && (
          <a href={`tel:${L.contact_phone}`} className="btn-pill btn-accent text-sm"
             data-testid={`teacher-phone-${teacher.id}`}>
            <Phone className="w-4 h-4" /> {tr("Call")}
          </a>
        )}
      </div>
    </div>
  );
}
